import { jsx as _jsx } from "react/jsx-runtime";
import { ThemeProvider } from '@kubit/web-ui-components/provider/themeProvider';
import { mergeObjectsStyles } from '@kubit/web-ui-components/utils/mergeObjectStyles';
import * as React from 'react';
import { getThemeConfig } from './config';
import { ReloadedThemeInfoProvider } from './reloadedThemeInfoContext';
import { reloadedThemes } from './types/reloadedProvider';
/**
 * Provides a context for Reloaded themes, allowing for theme switching and error display.
 *
 * @param {React.PropsWithChildren<{
 *   theme?: reloadedThemes; // The primary theme to be used.
 *   alternativeTheme?: reloadedThemes; // An alternative theme that can be switched to.
 *   showErrors?: boolean; // Flag to indicate whether to show errors.
 *   customFallback?: JSX.Element; // A custom fallback component to be used if the theme fails to load.
 * }>} props The props for the ReloadedProvider component.
 * @returns {React.JSX.Element} The ReloadedProvider component wrapped around its children.
 */
export const ReloadedProvider = ({ children, theme = reloadedThemes.flame_retail_light_regular, alternativeTheme, showErrors = false, customFallback, customStyles = {}, elementContainer, defaultReset = true, }) => {
    const config = getThemeConfig(theme, elementContainer);
    const mergedStyles = mergeObjectsStyles(config?.styles || {}, customStyles);
    // Determine the alternative theme based on the current theme or provided alternative
    const alternative = React.useMemo(() => {
        if (alternativeTheme)
            return alternativeTheme;
        if (theme.endsWith('_regular')) {
            const alternative = theme.replace('_regular', '_alternative');
            if (isValidTheme(alternative))
                return alternative;
        }
        else if (theme.endsWith('_alternative')) {
            const alternative = theme.replace('_alternative', '_regular');
            if (isValidTheme(alternative))
                return alternative;
        }
        // Fallback in case no valid alternative is provided or detected
        return theme;
    }, [theme, alternativeTheme]);
    return (_jsx(ReloadedThemeInfoProvider, { value: { theme, alternative, showErrors }, children: _jsx(ThemeProvider, { customFallback: customFallback, customFonts: config.customFonts, customGlobalStyles: config.customGlobalStyles, defaultReset: defaultReset, genericComponents: config.genericComponents, showErrors: showErrors, styles: mergedStyles, themeInformation: { name: config.themeInformation.name }, children: children }) }));
};
/**
 * Checks if a given theme is a valid `reloadedThemes` value.
 *
 * @param {string} theme The theme to validate.
 * @returns {boolean} `true` if the theme is valid, otherwise `false`.
 */
function isValidTheme(theme) {
    return Object.values(reloadedThemes).includes(theme);
}
//# sourceMappingURL=reloadedProvider.js.map